import { ReactNode, Component, MutableRefObject, LegacyRef } from 'react';

import { RelayProp, graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { Visibility } from '../../components/global/Visibility';
import { SharedRespRelatedItem } from '../../shared/SharedRespRelatedItem/SharedRespRelatedItem';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';

// helpers
import classnames from 'classnames';
import {
    trackSimilarItemClick,
    trackSimilarItemImpressions,
} from '../tracking/similarItemsTracking';
import { trackItemsDisplayed, trackItemClick } from '../../pdp/tracking/similarItemsTracking';

import { PdpRespSimilarItemsGrid_similarItems$data } from './__generated__/PdpRespSimilarItemsGrid_similarItems.graphql';
import { PdpRespSimilarItemsGrid_item$data } from './__generated__/PdpRespSimilarItemsGrid_item.graphql';
import { PdpRespSimilarItemsGrid_user$data } from './__generated__/PdpRespSimilarItemsGrid_user.graphql';
import { PdpRespSimilarItemsGrid_viewer$data } from './__generated__/PdpRespSimilarItemsGrid_viewer.graphql';
import { itemTileType } from '../../shared/SharedRespRelatedItem/SharedRespRelatedItem';

type GridItems = PdpRespSimilarItemsGrid_similarItems$data;
type Props = {
    processGridItems?: (items: GridItems) => GridItems;
    header: ReactNode;
    subHeader?: ReactNode;
    relayVariables: {
        isClient: boolean;
    };
    isDark?: boolean;
    hideBar?: boolean;
    showLightBox?: boolean;
    similarItems: GridItems;
    relay: RelayProp;
    currency?: string;
    gridItemWrapperClass?: string;
    gridWrapperClass?: string;
    gridRowWrapperClass?: string;
    gridHeaderClass?: string;
    user?: PdpRespSimilarItemsGrid_user$data | null;
    viewer?: PdpRespSimilarItemsGrid_viewer$data | null;
    gridLocation: string;
    favoritesLocation: string;
    fetchFolder?: boolean;
    dataTn?: string;
    scrollToRef?: MutableRefObject<HTMLDivElement | undefined>;
    gridItemWrapperRef?: MutableRefObject<HTMLDivElement | undefined>;
    item: PdpRespSimilarItemsGrid_item$data;
    onContentVisible?: () => void;
    onComponentDidMount?: () => void;
    showNoReserveBadge?: boolean;
    tileType?: itemTileType;
    hideTileImagePadding?: boolean;
    showMeasurements?: boolean;
};

type State = { contentIsVisible: boolean };
export class PdpRespSimilarItemsGridComponent extends Component<Props, State> {
    static defaultProps = {
        isDark: true,
        showLightBox: true,
        dataTn: 'pdp-similar-recommendation-grid',
        showNoReserveBadge: true,
    };

    constructor(props: Props) {
        super(props);

        this.onClickItem = this.onClickItem.bind(this);
        this.onContentVisible = this.onContentVisible.bind(this);
        this.hasFiredTracking = false;

        this.state = {
            contentIsVisible: false,
        };
    }

    componentDidMount(): void {
        if (this.props.onComponentDidMount) {
            this.props.onComponentDidMount();
        }
    }

    componentDidUpdate(): void {
        if (
            this.props?.similarItems?.[0]?.ecommerceTrackingParams &&
            this.state.contentIsVisible &&
            !this.hasFiredTracking
        ) {
            this.hasFiredTracking = true;
            if (this.props.onContentVisible) {
                this.props.onContentVisible();
            } else {
                trackItemsDisplayed({
                    moduleType: 'PDP_SIMILAR_ITEMS',
                    actionValue: 'bottom fold',
                });
            }
            trackSimilarItemImpressions(this.getGridItems(), this.props.gridLocation);
        }
    }

    hasFiredTracking: boolean;

    onContentVisible(): void {
        this.setState({ contentIsVisible: true });
    }

    onClickItem(index: number): void {
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS', actionValue: 'bottom fold' });
        trackSimilarItemClick(index, this.getGridItems()[index], this.props.gridLocation);
    }

    getGridItems(): GridItems {
        const gridItems = this.props?.similarItems || [];
        return this.props.processGridItems ? this.props.processGridItems(gridItems) : gridItems;
    }

    render(): ReactNode {
        const gridItems = this.getGridItems();

        if (!gridItems.length) {
            return null;
        }

        const { contentIsVisible } = this.state;
        const {
            isDark,
            currency = '',
            user = null,
            viewer = null,
            showLightBox,
            gridWrapperClass,
            gridRowWrapperClass,
            gridItemWrapperClass,
            gridHeaderClass,
            favoritesLocation,
            header,
            subHeader,
            dataTn,
            scrollToRef,
            item,
            gridItemWrapperRef,
            hideBar,
            showNoReserveBadge,
            tileType,
            hideTileImagePadding,
            showMeasurements,
        } = this.props;
        const { isClient } = this.props.relayVariables;
        const favoritesProviderProps = {
            location: favoritesLocation,
            disable: !isClient || !contentIsVisible,
            userId: user?.serviceId ?? null,
            environment: this.props.relay.environment,

            itemIds: gridItems.map(gridItem => gridItem.serviceId),
            fetchFolder: this.props.fetchFolder,
        };

        return (
            <PdpRespSimilarItemsContainer
                isDark={isDark}
                hideBar={isDark || hideBar}
                header={header}
                subHeader={subHeader}
                scrollToRef={scrollToRef}
                gridWrapperClass={gridWrapperClass}
                gridHeaderClass={gridHeaderClass}
                dataTn={dataTn}
                item={item}
            >
                <Visibility onContentVisible={this.onContentVisible} />
                <FavoritesProvider {...favoritesProviderProps}>
                    {/* favorites  - passed to PdpRespFavoritingItem in SharedRespRelatedItem */}
                    {({ props: favorites }: $TSFixMe) => (
                        <QuickViewProvider>
                            {/* quickView - passed to PdpRespSimilarItemQuickView in SharedRespRelatedItem */}
                            {({ quickView }) => {
                                const itemProps = {
                                    isClient,
                                    quickView,
                                    favorites,
                                    user,
                                    showLightBox,
                                    badgeBgColor: isDark ? 'white' : undefined,
                                    tileType,
                                    hideTileImagePadding,
                                    showMeasurements,
                                };

                                return (
                                    <div className={classnames('rowFlex', gridRowWrapperClass)}>
                                        {gridItems.map((gridItem, index) => (
                                            <div
                                                key={gridItem.serviceId}
                                                className={classnames(
                                                    'colLg3 colMd6 colXs6',
                                                    gridItemWrapperClass
                                                )}
                                                ref={
                                                    gridItemWrapperRef as LegacyRef<HTMLDivElement>
                                                }
                                            >
                                                {/* Grid of items */}
                                                <SharedRespRelatedItem
                                                    item={gridItem}
                                                    index={index}
                                                    onClick={() => this.onClickItem(index)}
                                                    currency={currency}
                                                    viewer={viewer}
                                                    showNoReserveBadge={showNoReserveBadge}
                                                    {...itemProps}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                );
                            }}
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </PdpRespSimilarItemsContainer>
        );
    }
}

export const PdpRespSimilarItemsGrid = createFragmentContainer(PdpRespSimilarItemsGridComponent, {
    viewer: graphql`
        fragment PdpRespSimilarItemsGrid_viewer on Viewer {
            ...SharedRespRelatedItem_viewer
        }
    `,
    similarItems: graphql`
        fragment PdpRespSimilarItemsGrid_similarItems on Item @relay(plural: true) {
            serviceId
            title
            browseUrl
            categoryCode
            ecommerceTrackingParams
            ...SharedRespRelatedItem_item
                @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
        }
    `,
    user: graphql`
        fragment PdpRespSimilarItemsGrid_user on User {
            serviceId
            ...SharedRespRelatedItem_user
        }
    `,
    item: graphql`
        fragment PdpRespSimilarItemsGrid_item on Item {
            ...PdpRespSimilarItemsContainer_item
        }
    `,
});
